<template>
    <wrapper-features-templates-one-column
    :nonAuthenticatedFeature="nonAuthenticatedRoute"
    :hideWhiteBoxOnLoad="showLoader || applyErrorState"
    :showSearchSlot="!showLoader && !applyErrorState"
    :noWhiteBox="!showLoader && !applyErrorState && mapView && filteredAgenciesList.length"
    :showLoader="showLoader"
    :applyErrorState="applyErrorState || (applyMapErrorState && mapView)"
	:errorStateMessage="applyMapErrorState ? t('agencies.errorStateMap') : t('agencies.errorState')"
    :showFiltersAndEmptyState="!showLoader && !applyErrorState && !filteredAgenciesList.length"
    :customBreadcrumbs="customBreadcrumbs"
    :showFloatingButton="showFilterButton"
    :floatingButtonParams="{ icon: 'filter', label: t('filters.filter') }"
    @floatingButtonClicked="showFilterModal = true"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- *********************************************
        *                    Search Bar                  *
        ********************************************** -->
        <template v-slot:top-search-area-slot>
            <div v-if="!showLoader && !applyErrorState" class="search-box" :class="{ 'mobile': getMobileAppState }">
                <input-search
                :params="{placeholder: t('agencies.searchPlaceholder')}"
                :preventInputClick="getMobileAppState"
                :inputValue="searchValue"
                @clicked="showMobileSearch = true"
                @input="[searchValue = $event, currentAmountOfAgencies = 10]"/>
                <switch-view mapIcon :isActive="!mapView" @clicked="mapView = !mapView"/>
            </div>
		</template>
        <!-- *********************************************
        *                    Agencies                    *
        ********************************************** -->
        <div
        v-if="!showLoader && !applyErrorState"
        :key="`tableIsEmpty${tableData.length === 0}+${mapView}`"
        class="content-wrapper prevent-auto-margins">
            <!-- *********************************************
            *                    Filter Tags                 *
            ********************************************** -->
            <chip-wrapper
            v-if="filterTags.length"
            :class="{'map-position': mapView && filteredAgenciesList.length, 'map-position-mobile': getMobileAppState }"
            :filters="filterTags"
            @close="removeFilterHandler"
            @closeAll="closeAllFiltersHandler"/>
            <!-- *********************************************
            *                  Agencies List                 *
            ********************************************** -->
            <transition name="common-enter-animation" mode="out-in" appear>
                <table-generic
                v-if="!mapView && tableData.length"
                :headerData="tableHeaderData"
                :tableStyles="tableStyles"
                :tableData="tableData"
                :hasMore="filteredAgenciesList.length > currentAmountOfAgencies"
                :isLoadingMore="tableLoadingMore"
                :loadMoreText="t('agencies.loadMoreText')"
                :loadingMoreText="t('agencies.loadingMoreText')"
                @rowClicked="tableClickHandler"
                @loadMore="loadMoreHandler"/>
            </transition>
        </div>
        <!-- Web Agencies Map -->
        <agencies-map
        v-if="mapView && !showLoader && !applyErrorState && filteredAgenciesList.length && !getMobileAppState"
        :agenciesList="filteredAgenciesList"
        :useMyCurrentLocation="useMyCurrentLocation"
        @openAgenciesDetail="mapClickHandler"
        @initalPositionMoved="useMyCurrentLocation = false"
        @mapError="mapErrorHandler"/>
        <!-- Mobile -->
        <div v-if="getMobileAppState" ref="mapPositionWrapper" class="map-position-wrapper prevent-auto-margins" :style="{'height': mapPositionHeight}">
            <!-- Agencies Map -->
            <agencies-map
            v-if="mapView && !showLoader && !applyErrorState && !applyMapErrorState && filteredAgenciesList.length"
            :agenciesList="filteredAgenciesList"
            :useMyCurrentLocation="useMyCurrentLocation"
            @openAgenciesDetail="mapClickHandler"
            @initalPositionMoved="useMyCurrentLocation = false"
            @mapError="mapErrorHandler"/>
        </div>
        <!-- *********************************************
        *                    Empty State                 *
        ********************************************** -->
		<template v-if="!showLoader && !applyErrorState && !filteredAgenciesList.length" #empty-state-slot>
            <empty-state-generic
            :illustration="emptyStateIlustration"
            :title="t('agencies.emptyState.title')"
            :description="t('agencies.emptyState.description')"/>
        </template>
    </wrapper-features-templates-one-column>
    <!-- Current Location Button -->
    <button-floating
    v-if="!showLoader && !applyErrorState && getMobileAppState && mapView"
    class="location-button"
    icon="location"
    variant="secondary"
    @clicked="useMyCurrentLocation = true"/>
    <!-- Agency Details -->
	<agency-detail-modal
	v-if="showDetailsModal"
	:agencyDetails="agencyDetails"
    :agenciesList="filteredAgenciesList"
	@hideMe="showDetailsModal = false"/>
    <!-- Agencies Filters -->
	<agencies-filter-modal
	v-if="showFilterModal"
	:appliedFilters="appliedFilters"
	@hideMe="showFilterModal = false"
	@applyFilters="applyFiltersHandler($event)"/>
    <!-- Search Modal for Mobile -->
    <modal-side-search
    v-if="showMobileSearch"
    :searchPlaceholder="t('agencies.searchPlaceholder')"
    :searchInputValue="searchValue"
    :applyEmptyState="!filteredAgenciesList.length"
    :triggerClose="triggerMobileSearchClose"
    @updateSearchValue="searchValue = $event"
    @hideMe="[showMobileSearch = false, triggerMobileSearchClose = false]">
        <!-- *********************************************
        *                    Filter Tags                 *
        ********************************************** -->
        <chip-wrapper
        v-if="filterTags.length"
        :style="{'padding-left': '16px'}"
        :filters="filterTags"
        @close="removeFilterHandler"
        @closeAll="closeAllFiltersHandler"/>
        <!-- Return to Map Button -->
        <button-tertiary
        v-if="mapView && searchValue.length && filteredAgenciesList.length"
        icon="pin-drop"
        :label="t('agencies.seeMap')"
        :class="{'adjust-margin': filterTags.length}"
        @clicked="triggerMobileSearchClose = true"/>
        <!-- Table Generic -->
        <table-generic
        :detailCustomIcon=" mapView ? 'pin-drop': null"
        :headerData="tableHeaderData"
        :tableStyles="tableStyles"
        :tableData="tableData"
        :hasMore="filteredAgenciesList.length > currentAmountOfAgencies"
        :isLoadingMore="tableLoadingMore"
        :loadMoreText="t('agencies.loadMoreText')"
        :loadingMoreText="t('agencies.loadingMoreText')"
        @rowClicked="mapView ? mapViewTableClickHandler($event) : tableClickHandler($event)"
        @loadMore="loadMoreHandler"/>
    </modal-side-search>
    <!-- Feedback Toast -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import InputSearch from '@/components/generic/inputs/Search'
import TableGeneric from '@/components/generic/table/Generic'
import SwitchView from '@/components/generic/switch/View'
import ChipWrapper from '@/components/generic/buttons/chip/ChipWrapper'
import ButtonFloating from '@/components/generic/buttons/Floating'
import AgenciesMap from '@/components/features/agencies/AgenciesMap'
import AgencyDetailModal from '@/components/features/agencies/AgencyDetailModal'
import AgenciesFilterModal from '@/components/features/agencies/AgenciesFilterModal'
import ModalSideSearch from '@/components/generic/modals/side/Search'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'

/* Vue & Helpers */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { nonAuthenticatedRoutes } from '@/router/helpers/validations'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions && mapGetters */
const { requestAgenciesList, requestAgenciesProvinces, requestNonAuthenticatedAgenciesList, requestNonAuthenticatedAgenciesProvinces, clearFeatureStates } = mapActions(moduleMapper.AGENCIES)
const { getAgenciesList, getAgenciesProvincesList } = mapGetters(moduleMapper.AGENCIES)
const { getMobileAppState } = mapGetters()

const showLoader = ref(false)
const applyErrorState = ref(false)
const changeToListIfError = ref(true)
const applyMapErrorState = ref(false)
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const mapView = ref(true)
const showDetailsModal = ref(false)
const showMobileSearch = ref(false)
const triggerMobileSearchClose = ref(false)
const agencyDetails = ref({})

/* Controls if current route is non authenticated */
const nonAuthenticatedRoute = computed(() => {
    return nonAuthenticatedRoutes().has(currentRoute.path)
})

const emptyStateIlustration = computed(() => {
    if (!filteredAgenciesList.value.length && filterTags.value.length) return 'emptyState/filters'

    return 'emptyState/search'
})
/**********************************************************************
*                                                                     *
*                            Table Data                               *
*                                                                     *
**********************************************************************/
const currentAmountOfAgencies = ref(10)
const tableLoadingMore = ref(false)

const tableHeaderData = [
    { label: t('agencies.tableHeader.name'), dataAttr: 'map-icon', showInMobile: true, rowCellType: 'agency-pin' },
	{ label: '', showInMobile: { groupWith: 'address' }, dataAttr: 'name', rowCellType: 'title' },
    { label: t('agencies.tableHeader.address'), dataAttr: 'address', rowCellType: 'description' }
]

const tableStyles = [null, { 'text-align': 'star' }, { 'text-align': 'star' }]

const tableData = computed(() => {
    const agenciesList = filteredAgenciesList.value

    return agenciesList.slice(0, parseInt(currentAmountOfAgencies.value)).map(agency => {
		return [
            { value: agency.id, dataAttr: 'id' },
            {
                type: 'agency-pin',
                value: { icon: 'map-pin-agency', agencyName: getMobileAppState.value ? '' : agency.name },
                dataAttr: 'map-icon'
            },
            { type: 'title', value: getMobileAppState.value ? agency.name : '', dataAttr: 'name' },
            {
                type: 'description',
                value: `${agency.address.address}, ${agency.address.city.toLowerCase().charAt(0).toUpperCase() + agency.address.city.toLowerCase().slice(1)}`,
                dataAttr: 'address'
            }
        ]
	})
})

const mapViewTableClickHandler = row => {
    searchValue.value = filteredAgenciesList.value.find(obj => obj.id === row.rowId).name
    triggerMobileSearchClose.value = true
}

const tableClickHandler = row => {
    const details = getAgenciesList.value.find(agency => agency.id === row.rowId)
	agencyDetails.value = details
	showDetailsModal.value = true
}

const loadMoreHandler = () => {
    tableLoadingMore.value = true
    setTimeout(() => {
        currentAmountOfAgencies.value += 10
        tableLoadingMore.value = false
    }, 1000)
}

/**********************************************************************
*                                                                     *
*                               Map                                   *
*                                                                     *
**********************************************************************/
const useMyCurrentLocation = ref(false)
const mapPositionWrapper = ref('')
const mapPositionHeight = ref(null)

const mapClickHandler = details => {
	agencyDetails.value = details
	showDetailsModal.value = true
}

const mapErrorHandler = () => {
    applyMapErrorState.value = true

    if (changeToListIfError.value) {
        mapView.value = false
        changeToListIfError.value = false
    }
}
/**********************************************************************
*                                                                     *
*                                Filters                              *
*                                                                     *
**********************************************************************/
const searchValue = ref('')
const appliedFilters = ref({ agenciesProvince: '' }) // Im letting this be a object in case in the future we have more than one filter
const showFilterModal = ref(false)

const showFilterButton = computed(() => {
    let emptyInputSearchList = false

    if (!!searchValue.value.length && !appliedFilters.value.agenciesProvince.length && !tableData.value.length) emptyInputSearchList = true

    return !showLoader.value && !applyErrorState.value && !emptyInputSearchList
})

const filteredAgenciesList = computed(() => {
    /* If no filter is applied we simply return all agencies */
    if (!appliedFilters.value.agenciesProvince.length && !searchValue.value.length) return getAgenciesList.value

	const agenciesList = appliedFilters.value.agenciesProvince.length ? getAgenciesList.value.filter(ele => ele.province === appliedFilters.value.agenciesProvince) : getAgenciesList.value

    return agenciesList.reduce((acc, agency) => {
        const searchValueLowered = searchValue.value.toLowerCase()

        const searchByName = agency.name.toLowerCase().includes(searchValueLowered)
        const searchByAddress = agency.address.address.toLowerCase().includes(searchValueLowered)
        if (searchByName || searchByAddress) acc.push(agency)
        return acc
    }, [])
})

const filterTags = ref([])

const applyFiltersHandler = (filters) => {
	filterTags.value = []

	for (const filter in filters) {
		if (filters[filter]) {
			appliedFilters.value[filter] = filters[filter]

			if (filterTags.value.find(tag => { return tag.id === filter })) {
                filterTags.value.find(tag => { return tag.id === filter }).text = filterTagTextHandler(filter, filters[filter])
			} else {
				filterTags.value.push({
					id: filter,
					label: t(`agencies.filters.${filter}`),
					text: filterTagTextHandler(filter, filters[filter])
				})
			}
		} else {
			appliedFilters.value[filter] = ''
		}
	}

    currentAmountOfAgencies.value = 10
}

const filterTagTextHandler = (filter, value) => {
	switch (filter) {
		case 'agenciesProvince':
			return getAgenciesProvincesList.value.find(type => type === value)
		default: return value
	}
}

const removeFilterHandler = (tagId) => {
	appliedFilters.value[tagId] = ''

	filterTags.value = filterTags.value.filter(filter => filter.id !== tagId)

    currentAmountOfAgencies.value = 10

    if (!filterTags.value.length) filterTags.value = []
}

const closeAllFiltersHandler = () => {
	filterTags.value = []

    currentAmountOfAgencies.value = 10

	for (const filter in appliedFilters.value) {
		appliedFilters.value[filter] = ''
    }
}

/**********************************************************************
*                                                                     *
*                              Requests                               *
*                                                                     *
**********************************************************************/
const initialRequests = async () => {
    showLoader.value = true
    showFeedbackToast.value = false
    applyErrorState.value = false

    try {
        nonAuthenticatedRoute.value ? await requestNonAuthenticatedAgenciesList() : await requestAgenciesList()
        nonAuthenticatedRoute.value ? await requestNonAuthenticatedAgenciesProvinces() : await requestAgenciesProvinces()

        showLoader.value = false
    } catch (error) {
        errorHandler(error.response.data, initialRequests)
        applyErrorState.value = true
    }
}

/**********************************************************************
*                             Error Handler                           *
**********************************************************************/
const errorHandler = (data, retryFn) => {
    showLoader.value = false
    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*              Mobile Specific Logic             *
*                                                *
*************************************************/
const currentRoute = useRoute()

const customBreadcrumbs = computed(() => {
    return getMobileAppState.value ? {
        currentArea: t('agencies.customBreadcrumbs')
    } : null
})

initialRequests()

onMounted(() => {
    if (getMobileAppState.value) mapPositionHeight.value = `${mapPositionWrapper.value.parentElement.parentElement.clientHeight - 72}px`
})

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.map-position-wrapper {
    width: 100%;

    position: relative;

    overflow: hidden;
    z-index: 1;
}

.search-box {
    width: 100%;
    gap:16px;
    flex-shrink: 0;

    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
}

.content-wrapper {
	@include commonEnteringAnimation;
    width: 100%;

    .chips-wrapper-container {
        height: 40px;
        margin-bottom: 16px;
    }

    .map-position-mobile {
        padding-left: 16px;
    }

    .map-position {
        position: absolute;
        left: 48px;
        margin-bottom: unset;
        margin-top: 24px;

        &.map-position-mobile {
            padding-left: 0;
            left: 16px;
        }
    }
}

.tertiary-button-wrapper {
    width: 100%;
    padding: 16px 16px 16px 8px;
}

.adjust-margin {
    padding-top: 0;
}

.location-button {
    position: fixed;
    right: 16px;
    bottom: 72px; // 16px from bottom, 48px from other floating bottom and 8px gap distance
    z-index: 11;
}
</style>
